import React, { useEffect, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import useTechCopy from "../useTechCopy";
import ShinyText from "../ui/ShinyText";

const VIDEO_SRC =
  "https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260328_105406_16f4600d-7a92-4292-b96e-b19156c7830a.mp4";

const EASE = [0.22, 1, 0.36, 1];

// Full-screen video hero. The fixed nav (h-20) sits over the top of it, so the
// content column starts below that band; the headline and CTA are centred in
// the remaining space.
export default function HeroSection() {
  const copy = useTechCopy();
  const reduced = useReducedMotion();
  const videoRef = useRef(null);

  useEffect(() => {
    if (reduced) videoRef.current?.pause();
  }, [reduced]);

  const rise = (delay) =>
    reduced
      ? {}
      : {
          initial: { opacity: 0, y: 12 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.7, delay, ease: EASE },
        };

  return (
    <section id="hero" className="relative h-screen min-h-[640px] overflow-hidden bg-black">
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        src={VIDEO_SRC}
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover"
      />

      <div className="relative z-10 h-full max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 pt-24 sm:pt-28 pb-10 flex flex-col">
        {/* Headline block */}
        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <motion.h1
            {...rise(0.2)}
            className="font-medium tracking-tighter leading-[0.85] text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl"
          >
            <span className="block text-white">{copy.hero.title[0]}</span>
            {/* Vertical padding keeps ascenders/descenders inside the painted
                background — background-clip: text hides anything outside it. */}
            <ShinyText text={copy.hero.title[1]} className="block py-[0.2em] -my-[0.2em]" />
          </motion.h1>

          <motion.div {...rise(0.4)} className="mt-10">
            <a
              href="#contact"
              className="group inline-flex items-center gap-2 rounded-full bg-black hover:bg-gray-900 px-6 md:px-8 py-3 md:py-4 text-sm md:text-base font-medium text-white transition-colors duration-300"
            >
              {copy.hero.cta}
              <ArrowRight
                className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
                aria-hidden="true"
              />
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
